<template>
  <main id="create-client-page" class="page-container">
    <button type="button" class="back-btn" @click="goBack">
      <i class="fa-solid fa-arrow-left"></i> Indietro
    </button>
    <form
      class="create-client-form"
      @submit.prevent="onCreateClient"
      novalidate
    >
      <!-- RIGA 1 -->
      <div class="create-client-row row-1">
        <div class="field">
          <label for="client-name">Nome cliente *</label>
          <input
            id="client-name"
            type="text"
            v-model="formData.clientName"
            placeholder="inserisci il nome del cliente..."
          />
        </div>

        <div class="field">
          <label for="fiscal-code">Codice fiscale *</label>
          <input
            id="fiscal-code"
            type="text"
            v-model="formData.taxCode"
            placeholder="inserisci il codice fiscale..."
          />
        </div>
      </div>

      <!-- RIGA 2: telefono + recapito -->
      <div class="create-client-row row-2">
        <div class="field">
          <label for="phone">Telefono *</label>
          <input
            id="phone"
            type="text"
            v-model="formData.phoneNumber"
            placeholder="inserisci il numero di telefono..."
          />
        </div>

        <!-- [sc-48860] recapito + PEC/email larghi quanto un campo: allineati alle altre colonne -->
        <div class="recapito-group">
          <div class="field contact-selector">
            <label>Recapito preferito</label>
            <StatusSelect v-model="contactChoice" :options="contactOptions" />
          </div>

          <div class="field" v-if="contactChoice === 'pec'">
            <small v-if="erroreRecapito" class="help-error">{{
              erroreRecapito
            }}</small>
            <small v-else-if="avvisoPec" class="help-warning">{{
              avvisoPec
            }}</small>
            <label for="pec">PEC</label>
            <input
              id="pec"
              type="email"
              v-model="formData.pec"
              placeholder="inserisci l'indirizzo PEC..."
            />
          </div>

          <div class="field" v-if="contactChoice === 'email'">
            <small v-if="erroreRecapito" class="help-error">{{
              erroreRecapito
            }}</small>
            <label for="email">Email</label>
            <input
              id="email"
              type="email"
              v-model="formData.email"
              placeholder="inserisci l'indirizzo email..."
            />
          </div>
        </div>
      </div>

      <!-- RIGA 2b: indirizzi proposti come consegna nei preventivi [sc-48860] -->
      <div class="create-client-row row-2b">
        <div class="field">
          <label for="residenza">Indirizzo di residenza</label>
          <input
            id="residenza"
            type="text"
            v-model="formData.residenceAddress.address"
            placeholder="Via, civico, città…"
            autocomplete="off"
          />
        </div>
        <div class="field">
          <label for="cantiere">Indirizzo di cantiere</label>
          <input
            id="cantiere"
            type="text"
            v-model="formData.worksiteAddress.address"
            placeholder="Via, civico, città…"
            autocomplete="off"
          />
        </div>
      </div>

      <hr />

      <!-- RIGA 3: ragione sociale + p.iva -->
      <div class="create-client-row row-3">
        <div class="field">
          <label for="business-name">Ragione sociale</label>
          <input
            id="business-name"
            type="text"
            v-model="formData.companyName"
            placeholder="inserisci la ragione sociale..."
          />
        </div>

        <div class="field">
          <label for="vat-number">P.IVA</label>
          <input
            id="vat-number"
            type="text"
            v-model="formData.vatNumber"
            placeholder="inserisci la partita IVA..."
          />
        </div>
      </div>

      <!-- RIGA 4: codice univoco + sede legale -->
      <div class="create-client-row row-4">
        <div class="field">
          <label for="unique-code">Codice univoco</label>
          <input
            id="unique-code"
            type="text"
            v-model="formData.uniqueCode"
            placeholder="inserisci il codice univoco..."
          />
        </div>

        <!-- Sede legale, scritta a mano [sc-48762] -->
        <div class="field">
          <label for="sede-legale">Sede legale</label>
          <input
            id="sede-legale"
            ref="lhInput"
            type="text"
            v-model="lhInput"
            placeholder="Via, civico, città…"
            autocomplete="off"
            inputmode="text"
            @input="onLHTyping"
          />
        </div>
      </div>

      <hr />

      <div class="save-button-container">
        <button
          id="create-client-button"
          class="primary-btn"
          type="submit"
          :disabled="!canSave"
          :title="!canSave ? titoloSalva : 'Crea cliente'"
        >
          Salva
        </button>
      </div>
    </form>

    <VueFinalModal
      v-model="showBackModal"
      :clickToClose="true"
      :escToClose="true"
      classes="documents-modal"
      content-class="confirm-modal-content"
    >
      <template #default>
        <button
          class="modal__close icons-button"
          @click="showBackModal = false"
          title="Chiudi"
        >
          <i class="fa-solid fa-xmark"></i>
        </button>
        <h3 class="modal__title">Tornare indietro?</h3>
        <p class="modal__subtitle">Le modifiche non salvate andranno perse.</p>
        <div class="modal-actions">
          <button class="secondary-btn" @click="showBackModal = false">
            Annulla
          </button>
          <button class="primary-btn confirm-btn" @click="confirmGoBack">
            Conferma
          </button>
        </div>
      </template>
    </VueFinalModal>
  </main>
</template>

<script>
import { mapActions } from "vuex/dist/vuex.cjs.js";
import StatusSelect from "@/components/StatusSelect.vue";
import { linkMaps } from "@/utilities/indirizzo";
import {
  emailValida,
  pecSospetta,
  obbligatoriCliente,
} from "@/utilities/cliente";

// helper per JSON o oggetto (coerente con CreateQuotePage)
const asLoc = (raw) => {
  if (!raw) return null;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return raw;
};

export default {
  name: "CreateClientPage",
  components: { StatusSelect },
  beforeRouteLeave(to) {
    if (this.navigationConfirmed || to.name === "login") {
      this.navigationConfirmed = false;
      return true;
    }
    this.pendingNavigation = to;
    this.showBackModal = true;
    return false;
  },
  data() {
    return {
      showBackModal: false,
      pendingNavigation: null,
      navigationConfirmed: false,

      contactChoice: "",
      contactOptions: [
        { value: "pec", label: "PEC", icon: "fa-shield-halved" },
        { value: "email", label: "Email", icon: "fa-envelope" },
      ],
      // input “visuale” della sede legale
      lhInput: "",

      formData: {
        clientName: "",
        taxCode: "",
        phoneNumber: "",
        uniqueCode: "",
        companyName: "",
        vatNumber: "",
        pec: "",
        email: "",
        legalHeadquarters: {
          name: "",
          address: "",
          link: "",
        },
        // [sc-48860] proposti come indirizzo di consegna nei preventivi
        residenceAddress: { address: "" },
        worksiteAddress: { address: "" },
      },
      errorMessage: "",
      successMessage: "",
    };
  },
  computed: {
    // [sc-48860] obbligatori nome, codice fiscale e telefono; il resto facoltativo
    mancanti() {
      return obbligatoriCliente(this.formData);
    },
    // PEC/email facoltative, ma se compilate devono avere un formato valido
    erroreRecapito() {
      const valore = this.contactChoice
        ? this.formData[this.contactChoice]
        : "";
      if (!valore || emailValida(valore)) return "";
      return this.contactChoice === "pec"
        ? "Formato PEC non valido."
        : "Formato email non valido.";
    },
    // Avviso che non blocca: i gestori PEC sono tanti, il dominio si segnala soltanto
    avvisoPec() {
      return this.contactChoice === "pec" && pecSospetta(this.formData.pec)
        ? "Il dominio non sembra di una PEC, controlla."
        : "";
    },
    canSave() {
      return this.mancanti.length === 0 && !this.erroreRecapito;
    },
    titoloSalva() {
      return this.mancanti.length
        ? `Compila: ${this.mancanti.join(", ")}`
        : this.erroreRecapito;
    },
  },
  watch: {
    contactChoice(newVal) {
      if (newVal === "pec") this.formData.email = "";
      if (newVal === "email") this.formData.pec = "";
    },
  },
  methods: {
    goBack() {
      this.pendingNavigation = null;
      this.showBackModal = true;
    },
    confirmGoBack() {
      this.navigationConfirmed = true;
      this.showBackModal = false;
      if (this.pendingNavigation) {
        this.$router.push(this.pendingNavigation);
      } else {
        // all'elenco, non alla pagina prima nella cronologia: dopo un
        // ricaricamento era di nuovo questa
        this.$router.push({ name: "customers" });
      }
    },
    ...mapActions(["createClient"]),

    onLHTyping() {
      this.formData.legalHeadquarters = {
        name: "",
        address: this.lhInput,
        link: linkMaps(this.lhInput),
      };
    },

    resetLH() {
      this.formData.legalHeadquarters = { name: "", address: "", link: "" };
      this.lhInput = "";
    },

    // ---- Submit ----
    async onCreateClient() {
      try {
        this.successMessage = "";
        this.errorMessage = "";

        if (!this.canSave) {
          this.$toast.error(this.titoloSalva);
          return;
        }

        // payload: JSON diretto, niente stringify
        const payload = { ...this.formData };
        if (this.contactChoice !== "email") delete payload.email;
        if (this.contactChoice !== "pec") delete payload.pec;

        const response = await this.$store.dispatch("createClient", payload);

        if (response.status === 200 || response.status === 201) {
          this.$toast.success(
            response.data?.message || "Cliente creato con successo!"
          );

          // reset form
          this.contactChoice = "";
          this.formData = {
            clientName: "",
            taxCode: "",
            phoneNumber: "",
            uniqueCode: "",
            companyName: "",
            vatNumber: "",
            pec: "",
            email: "",
            legalHeadquarters: {
              name: "",
              address: "",
              link: "",
            },
            residenceAddress: { address: "" },
            worksiteAddress: { address: "" },
          };
          this.resetLH();

          setTimeout(() => {
            this.$router.push({ name: "customers" });
          }, 800);
        }
      } catch (error) {
        if (error.response?.status === 409 || error.response?.status === 400) {
          // l'API mette il motivo in `message` (es. codice fiscale già usato)
          this.$toast.error(
            error.response.data?.message || "Dati del cliente non validi."
          );
        } else {
          this.$toast.error(
            error.response?.data?.details ||
              error.response?.data?.error ||
              "Errore durante la creazione del cliente."
          );
        }
      }
    },
  },
  mounted() {
    this.$nextTick(() => {
      // se stai arrivando da una modifica parziale, popola lhInput
      const lh = asLoc(this.formData?.legalHeadquarters);
      this.lhInput = lh?.address || "";
    });
  },
};
</script>

<style src="@/css/CreateClientPage.css"></style>
